import type {
  AckResult,
  ClientEventName,
  ClientInput,
  ClientToServerEvents,
  ServerToClientEvents,
} from '@sw/protocol';
import { PROTOCOL_VERSION } from '@sw/protocol';
import type { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';
import { SERVER_URL } from '../config';
import { useChat } from '../stores/chat';
import { useGame } from '../stores/game';
import { useRoom } from '../stores/room';
import { useSession } from '../stores/session';
import { bindHandlers } from './handlers';

type ClientSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

let socket: ClientSocket | null = null;
let manualRetry: number | null = null;
let retryDelay = 1000;

function scheduleManualReconnect() {
  if (manualRetry !== null || !socket) return;
  manualRetry = window.setTimeout(() => {
    manualRetry = null;
    if (socket && !socket.connected && useSession.getState().status !== 'replaced') socket.connect();
    retryDelay = Math.min(retryDelay * 2, 10_000);
  }, retryDelay);
}

/** Conecta (o reconecta) el socket con el token de la sesión guardada. */
export function connectSocket(): void {
  const { token } = useSession.getState();
  if (!token) return;
  if (socket) {
    if (!socket.connected) socket.connect();
    return;
  }
  const s: ClientSocket = io(SERVER_URL, {
    auth: { token, protocol: PROTOCOL_VERSION },
    transports: ['websocket'],
    reconnectionDelay: 800,
    reconnectionDelayMax: 8000,
  });
  socket = s;
  useSession.getState().setStatus('connecting');

  s.on('connect', () => {
    retryDelay = 1000;
  });
  s.on('connect_error', (err) => {
    if (err.message === 'INVALID_SESSION') {
      disconnectSocket();
      useSession.getState().clear();
      return;
    }
    if (err.message === 'UPGRADE_REQUIRED') {
      useSession.getState().setStatus('upgrade');
      s.disconnect();
      return;
    }
    useSession.getState().setStatus('reconnecting');
    // Si el rechazo vino del middleware, Socket.IO no reintenta solo.
    if (!s.active) scheduleManualReconnect();
  });
  s.on('disconnect', (reason) => {
    if (useSession.getState().status === 'replaced') return;
    useSession.getState().setStatus('reconnecting');
    if (reason === 'io server disconnect') scheduleManualReconnect();
  });
  s.on('session:replaced', () => {
    useSession.getState().setStatus('replaced');
  });
  bindHandlers(s);
}

export function disconnectSocket(): void {
  if (manualRetry !== null) window.clearTimeout(manualRetry);
  manualRetry = null;
  socket?.removeAllListeners();
  socket?.disconnect();
  socket = null;
  useRoom.getState().setRoom(null);
  useGame.getState().clear();
  useChat.getState().setHistory([]);
}

/** «Usar aquí»: recupera la sesión que otra pestaña había tomado. */
export function reclaimSession(): void {
  useSession.getState().setStatus('connecting');
  socket?.connect();
}

export async function emit<E extends ClientEventName>(event: E, payload: ClientInput<E>): Promise<AckResult<unknown>> {
  if (!socket || !socket.connected) return { ok: false, error: { code: 'INTERNAL', message: 'offline' } };
  const s = socket as unknown as { timeout(ms: number): { emitWithAck(e: string, p: unknown): Promise<AckResult<unknown>> } };
  try {
    return await s.timeout(8000).emitWithAck(event, payload);
  } catch {
    return { ok: false, error: { code: 'INTERNAL', message: 'timeout' } };
  }
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && socket && !socket.connected && useSession.getState().status !== 'replaced') {
      socket.connect();
    }
  });
}
